/**
 * @ Author: Housefolios
 * @ Create Time: 2022-06-28 11:54:04
 * @ Modified by: David Helmick
 * @ Modified time: 2025-10-27 10:42:48
 * @ Description: The render input function
 */

import { Fragment } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// Centralized lodash imports - much cleaner!
import {
  map,
  includes,
  find,
  cloneDeep,
  pullAt,
  concat,
  isNil,
  filter,
} from '@/utils/lodash'

import {
  Grid,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  Menu,
  MenuItem,
  Slider,
  TextField,
  Tooltip,
  Typography,
  Box,
} from '@mui/material'

import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import FileUpload from '@/housefolios-components/PropertyContainer/PropertyDashboard/FileUpload'
import Autocomplete from '@mui/material/Autocomplete'
import RemoveOption from '@/housefolios-components/Dialogs/RemoveOption'
import AddOption from '@/housefolios-components/Dialogs/AddOption'
import { NumericFormat } from 'react-number-format'
import { replaceStrategyName } from '@/utils/stratagies'
import { RehabCalcInputField } from '@/utils/rehabCalc'

/** Compact assumption-lookup control (original Bamburgh was d-30 / 30px). */
const definitionLookupBtnSx = {
  width: 22,
  height: 22,
  minWidth: 22,
  p: 0,
  borderRadius: '0.29rem',
  border: 0,
  color: '#fff',
  bgcolor: '#4191ff',
  '&:hover': { bgcolor: '#2f7cf0' },
}

function DefinitionLookupButton({
  section,
  disabled,
  handleDefinition,
  dataTour,
}) {
  return (
    <Tooltip title={section.tooltipButtonText || ''} followCursor>
      <IconButton
        data-tour={dataTour}
        onClick={(event) =>
          section.disabled || disabled
            ? null
            : handleDefinition(
                section.definition,
                event.target,
                section.definitionTarget,
              )
        }
        size="small"
        sx={definitionLookupBtnSx}
      >
        <FontAwesomeIcon
          data-tour={dataTour}
          icon={['fas', 'database']}
          style={{ fontSize: '0.7rem' }}
        />
      </IconButton>
    </Tooltip>
  )
}

const currencyFormater = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const formatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
})

/** Keep MUI Select on a listed option or ''. */
function resolveSelectValue(section) {
  const selectOptions = concat(
    section.dynamicMemberOptions,
    section.memberOptions,
    section.options,
  )
  if (includes(selectOptions, section.value)) {
    return section.value
  }
  if (
    !isNil(section.value) &&
    section.value !== '' &&
    includes(selectOptions, Number(section.value))
  ) {
    return Number(section.value)
  }
  if (
    !isNil(section.value) &&
    find(selectOptions, { value: section.value })
  ) {
    return section.value
  }
  if (
    !isNil(section.value) &&
    section.value !== '' &&
    find(selectOptions, { value: Number(section.value) })
  ) {
    return Number(section.value)
  }
  return ''
}

const renderInput = (
  section,
  member,
  disabled,
  onChangeFunctions,
  dropdownProps,
) => {
  const {
    onChangeProperty,
    onChangeAnalysis,
    onChangeAssumptions,
    onChangeLot,
    onChangeLotProcess,
    onChangeAsset,
    onChangeOffer,
    onChangeExchange,
  } = onChangeFunctions
  const {
    showFormatDropdown,
    anchorElAssumptions,
    toggleAssumptionsMenuFormat,
    handleAssumptionsMenuClose,
    handleAdornmentChange,
    handleAnalysisAdornmentChange,
    handleDefinition,
  } = dropdownProps
  const showDefinitionLookup = Boolean(section.definition) && !disabled
  const marks = [
    {
      value: section.min,
      label: section.minLabel,
    },
    {
      value: section.max,
      label: section.maxLabel,
    },
  ]

  return section.display ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      container
      sx={{
        justifyContent: 'space-between',
      }}
    >
      <Grid>
        <Typography>
          {replaceStrategyName(section.label, member)}
        </Typography>
      </Grid>
      <Grid>
        <Typography>
          {section.type === 'dollar'
            ? currencyFormater.format(section.value) || '-'
            : section.value || '-'}
          {section.type === 'percentage' ? '%' : ''}
        </Typography>
      </Grid>
    </Grid>
  ) : section.type === 'file' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
    >
      <FileUpload
        documentId={section.documentId}
        files={section.value}
        uploadFile={section.uploadFile}
        deleteFile={section.deleteFile}
        button
        filetypes={section.filetypes}
        notMulti={!section.multiple}
        shared={disabled}
        collection={section.collection}
        buttonText={section.buttonText}
      />
    </Grid>
  ) : section.type === 'rehabCalc' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
    >
      <RehabCalcInputField
        value={section.value}
        label={section.label}
        name={section.name}
        property={section.property}
        assumptionsInput={section.assumptionsInput}
        analysisInput={section.analysisInput}
        onChangeAnalysis={onChangeAnalysis}
        onChangeAssumptions={onChangeAssumptions}
        disabled={disabled}
        adornmentNumber={section.adornmentNumber}
      />
    </Grid>
  ) : section.type === 'budgetLines' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      data-hf-grid="keep"
      sx={{ width: '100%' }}
    >
      <Typography sx={{ py: 1, px: 0 }}>
        {replaceStrategyName(section.label, member)}
      </Typography>
      <Divider />
      <div
        style={{
          maxHeight: section?.showall ? '' : '230px',
          overflow: section?.showall ? '' : 'scroll',
        }}
      >
        {map(section.value, (item, index) => (
          <Grid
            container
            spacing={1}
            key={`${section.name}-${index}`}
            sx={{
              alignItems: 'center',
              width: '100%',
              py: 0.5,
              flexWrap: 'nowrap',
            }}
          >
            <Grid sx={{
              width: "auto",
              flex: "0 0 auto",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <IconButton
                size="small"
                disabled={section.disabled || disabled}
                onClick={() => {
                  const newValues = cloneDeep(section.value || [])
                  pullAt(newValues, index)
                  const event = {
                    target: {
                      name: section.name,
                      value: newValues,
                      type: 'array',
                    },
                    preventDefault: () => null,
                  }
                  if (section.disabled || disabled) return null
                  if (section.lotProcess) onChangeLotProcess(event)
                  else if (section.offer) onChangeOffer(event)
                  else if (section.asset) onChangeAsset(event)
                  else if (section.assumptions) onChangeAssumptions(event)
                  else if (section.lot) onChangeLot(event)
                  else if (section.details) {
                    onChangeProperty(
                      event,
                      section.definition ? 'userInput' : null,
                    )
                  } else {
                    onChangeAnalysis(
                      event,
                      section.definition ? 'userInput' : null,
                    )
                  }
                }}
                sx={{
                  width: 30,
                  height: 30,
                  p: 0,
                  color: "#4f5062",
                }}
              >
                <FontAwesomeIcon
                  icon={['far', 'circle-xmark']}
                  style={{ fontSize: '1rem' }}
                />
              </IconButton>
            </Grid>
            <Grid size="grow">
              <TextField
                name={section.name + `.${index}.type`}
                select={!section.disabled}
                fullWidth={true}
                variant="outlined"
                margin="dense"
                label="Type"
                value={
                  includes(
                    concat(section.memberOptions, section.options),
                    item.type,
                  ) ||
                  find(
                    concat(section.memberOptions, section.options),
                    { value: item.type },
                  )
                    ? item.type
                    : ''
                }
                disabled={disabled || section.disabled}
                required={
                  section.required === true ||
                  section.required === 'true'
                }
                onChange={(event) =>
                  section.onChange
                    ? section.onChange(event)
                    : section.lotProcess
                      ? onChangeLotProcess(event, true)
                      : section.offer
                        ? onChangeOffer(event, true)
                        : section.asset
                          ? onChangeAsset(event)
                          : section.assumptions
                            ? onChangeAssumptions(event, true)
                            : section.lot
                              ? onChangeLot(event, null, true)
                              : section.details
                                ? onChangeProperty(event, null, true)
                                : onChangeAnalysis(event, null, true)
                }
                slotProps={{
                  select: {
                    renderValue: (x) => {
                      let option = find(
                        concat(
                          section.memberOptions,
                          section.options,
                        ),
                        { value: x },
                      )
                      if (option) return option.label
                      else if (
                        includes(
                          concat(
                            section.memberOptions,
                            section.options,
                          ),
                          x,
                        )
                      )
                        return x
                      else return null
                    },
                  },
                }}
              >
                {map(section.memberOptions, (option) =>
                  option.value ? (
                    option.tip ? (
                      <Tooltip
                        title={option.tip}
                        key={option.value}
                        value={option.value}
                        followCursor
                      >
                        <MenuItem>
                          <Grid
                            container
                            sx={{
                              justifyContent: 'space-between',
                            }}
                          >
                            <Grid>
                              <Typography>{option.title}</Typography>
                            </Grid>
                            <Grid>
                              <Typography>{option.label}</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>
                      </Tooltip>
                    ) : (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        <Grid
                          container
                          sx={{
                            alignItems: 'center',
                          }}
                        >
                          <Grid>{option.label}</Grid>
                          <Typography style={{ flex: 1 }} />
                          <Typography>MO</Typography>
                        </Grid>
                      </MenuItem>
                    )
                  ) : (
                    <MenuItem key={option} value={option}>
                      <Grid
                        container
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Grid>{option}</Grid>
                        <Typography style={{ flex: 1 }} />
                        <Typography>MO</Typography>
                      </Grid>
                    </MenuItem>
                  ),
                )}
                {map(section.options, (option) =>
                  option.value ? (
                    option.tip ? (
                      <Tooltip
                        title={option.tip}
                        key={option.value}
                        value={option.value}
                        followCursor
                      >
                        <MenuItem>
                          <Grid
                            container
                            sx={{
                              justifyContent: 'space-between',
                            }}
                          >
                            <Grid>
                              <Typography>{option.title}</Typography>
                            </Grid>
                            <Grid>
                              <Typography>{option.label}</Typography>
                            </Grid>
                            <Grid>
                              <Typography>MO</Typography>
                            </Grid>
                          </Grid>
                        </MenuItem>
                      </Tooltip>
                    ) : (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                      >
                        <Grid
                          container
                          sx={{
                            alignItems: 'center',
                          }}
                        >
                          <Grid>{option.label}</Grid>
                          <Typography style={{ flex: 1 }} />
                          {section.editOption &&
                            (option.active ||
                              isNil(option.active)) && (
                              <AddOption
                                currentValue={
                                  includes(
                                    section.options,
                                    item.type,
                                  ) ||
                                  find(section.options, {
                                    value: item.type,
                                  })
                                    ? item.type
                                    : ''
                                }
                                addOption={section.optionName}
                                currentOption={option}
                                edit
                                addOptionLabel={
                                  section.label + ' Type'
                                }
                                addOptionTarget={
                                  section.name + '.' + index + '.type'
                                }
                                onChangeAnalysis={onChangeAnalysis}
                                onChangeLotProcess={
                                  onChangeLotProcess
                                }
                                onChangeProperty={onChangeProperty}
                                onChangeOffer={onChangeOffer}
                                onChangeAssumptions={
                                  onChangeAssumptions
                                }
                              />
                            )}
                          {section.removeOption &&
                            (option.active ||
                              isNil(option.active)) && (
                              <RemoveOption
                                removeOption={section.optionName}
                                removeOptionLabel={
                                  section.label + ' Type'
                                }
                                removeOptionValue={option.value}
                                removeOptionItemLabel={option.label}
                              />
                            )}
                        </Grid>
                      </MenuItem>
                    )
                  ) : (
                    <MenuItem key={option} value={option}>
                      <Grid
                        container
                        sx={{
                          alignItems: 'center',
                        }}
                      >
                        <Grid>{option}</Grid>
                        <Typography style={{ flex: 1 }} />
                        {section.editOption &&
                          (option.active || isNil(option.active)) && (
                            <AddOption
                              currentValue={
                                includes(
                                  section.options,
                                  item.type,
                                ) ||
                                find(section.options, {
                                  value: item.type,
                                })
                                  ? item.type
                                  : ''
                              }
                              addOption={section.optionName}
                              currentOption={option}
                              edit
                              addOptionLabel={section.label + ' Type'}
                              addOptionTarget={
                                section.name + '.' + index + '.type'
                              }
                              onChangeAnalysis={onChangeAnalysis}
                              onChangeLotProcess={onChangeLotProcess}
                              onChangeProperty={onChangeProperty}
                              onChangeOffer={onChangeOffer}
                              onChangeAssumptions={
                                onChangeAssumptions
                              }
                            />
                          )}
                        {section.removeOption &&
                          (option.active || isNil(option.active)) && (
                            <RemoveOption
                              removeOption={section.optionName}
                              removeOptionLabel={
                                section.label + ' Type'
                              }
                              removeOptionValue={option}
                            />
                          )}
                      </Grid>
                    </MenuItem>
                  ),
                )}
                {section.optionName && (
                  <AddOption
                    currentValue={
                      includes(section.options, item.type) ||
                      find(section.options, { value: item.type  })
                        ? item.type
                        : ''
                    }
                    addOption={section.optionName}
                    addOptionLabel={section.label + ' Type'}
                    addOptionTarget={
                      section.name + '.' + index + '.type'
                    }
                    onChangeAnalysis={onChangeAnalysis}
                    onChangeLotProcess={onChangeLotProcess}
                    onChangeProperty={onChangeProperty}
                    onChangeOffer={onChangeOffer}
                    onChangeAssumptions={onChangeAssumptions}
                  />
                )}
              </TextField>
            </Grid>
            <Grid size="grow">
              <NumericFormat
                customInput={TextField}
                thousandSeparator={true}
                prefix={'$'}
                fullWidth={true}
                decimalScale={0}
                key={section.name}
                name={section.name + `.${index}.value`}
                value={Math.round(item.value) || ''}
                disabled={section.disabled || disabled}
                onValueChange={(values) => {
                  const { value } = values
                  const event = {
                    target: {
                      name: section.name + `.${index}.value`,
                      value: value,
                      type: 'number',
                    },
                    preventDefault: () => {
                      return null
                    },
                  }
                  if (section.disabled || disabled) return null
                  if (section.lotProcess) onChangeLotProcess(event)
                  else if (section.offer) onChangeOffer(event)
                  else if (section.asset) onChangeAsset(event)
                  else if (section.assumptions)
                    onChangeAssumptions(event)
                  else if (section.lot) onChangeLot(event)
                  else if (section.details)
                    onChangeProperty(
                      event,
                      section.definition ? 'userInput' : null,
                    )
                  else
                    onChangeAnalysis(
                      event,
                      section.definition ? 'userInput' : null,
                    )
                  //onChange(event, saveAnalysis)
                }}
                type={'text'}
                onFocus={(event) => {
                  event.target.select()
                }}
                variant="outlined"
                margin="dense"
                label="Amount"
                fontSize={section.font}
                required={
                  section.required === true ||
                  section.required === 'true'
                }
                helperText={
                  <Typography
                    color="primary"
                    variant="caption"
                    style={
                      section.helperStyle ? section.helperStyle : null
                    }
                  >
                    {section.helper}
                  </Typography>
                }
                slotProps={{
                  input: {
                    startAdornment: section.adornmentNumber ? (
                      <InputAdornment
                        position="start"
                        sx={{ marginRight: 0.25 }}
                      >
                        <Typography
                          sx={{
                            width: 20, // Smaller size
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: 'black', // Black background
                            color: 'white', // White text
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 'bold',
                            fontSize: '0.75rem', // Smaller text
                            padding: 0, // Remove padding
                            textAlign: 'center', // Ensure centered alignment
                            marginLeft: '-6px', // Adjust spacing on the left
                          }}
                        >
                          {section.adornmentNumber}
                        </Typography>
                      </InputAdornment>
                    ) : null,
                    endAdornment: showDefinitionLookup ? (
                      <InputAdornment position="end">
                        <DefinitionLookupButton
                          section={section}
                          disabled={disabled}
                          handleDefinition={handleDefinition}
                        />
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
            </Grid>
          </Grid>
        ))}
      </div>
      <Typography
        variant="subtitle2"
        onClick={() => {
          let newValues = cloneDeep(section.value || [])
          newValues.push({
            type: '',
            value: 0,
          })
          const event = {
            target: {
              name: section.name,
              value: newValues,
              type: 'array',
            },
            preventDefault: () => {
              return null
            },
          }
          if (section.disabled || disabled) return null
          if (section.lotProcess) onChangeLotProcess(event)
          else if (section.offer) onChangeOffer(event)
          else if (section.asset) onChangeAsset(event)
          else if (section.assumptions) onChangeAssumptions(event)
          else if (section.lot) onChangeLot(event)
          else if (section.details)
            onChangeProperty(
              event,
              section.definition ? 'userInput' : null,
            )
          else
            onChangeAnalysis(
              event,
              section.definition ? 'userInput' : null,
            )
        }}
        sx={{
          py: 1.25,
          px: 0,
          cursor:
            section.disabled || disabled ? 'default' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          textAlign: 'left',
        }}
      >
        <FontAwesomeIcon icon={['fas', 'plus']} style={{ mr: '0.25rem' }} sx={{ fontSize: "0.875rem !important" }} />
        Add Line Item
      </Typography>
      <Divider />
    </Grid>
  ) : section.type === 'slider' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      style={{ marginLeft: 10, marginRight: 10 }}
      data-tour={section.dataTour}
    >
      <Typography
        style={{ color: '#9198a8', fontSize: 14, marginTop: 5 }}
      >
        {replaceStrategyName(section.label, member)}
      </Typography>
      <Slider
        track="inverted"
        valueLabelDisplay="auto"
        //getAriaValueText={section.label}
        onChange={(e, value) => {
        const event = {
        target: {
        name: section.name,
        value: value,
        type: 'number',
        },
        preventDefault: () => {
        return null
        },
        }
        section.lotProcess
        ? onChangeLotProcess(event)
        : section.offer
        ? onChangeOffer(event)
        : section.asset
        ? onChangeAsset(event)
        : section.assumptions
        ? onChangeAssumptions(event)
        : section.lot
        ? onChangeLot(event)
        : section.details
        ? onChangeProperty(event)
        : onChangeAnalysis(event)
        }}
        onChangeCommitted={(e, value) => {
        const event = {
        target: {
        name: section.name,
        value: value,
        type: 'number',
        },
        preventDefault: () => {
        return null
        },
        }
        section.lotProcess
        ? onChangeLotProcess(event, true)
        : section.offer
        ? onChangeOffer(event, true)
        : section.asset
        ? onChangeAsset(event)
        : section.assumptions
        ? onChangeAssumptions(event, true)
        : section.lot
        ? onChangeLot(event)
        : section.details
        ? onChangeProperty(event, null, true)
        : onChangeAnalysis(event, null, true)
        }}
        value={Number(section.value)}
        min={section.min}
        max={section.max}
        step={section.step ? section.step : 1}
        valueLabelFormat={
        section.valueLabelFormat
        ? section.valueLabelFormat
        : (x) => (x === section.max ? `${x}+` : `${x}%`)
        }
        marks={marks}
        disabled={section.disabled || disabled}
        />
    </Grid>
  ) : section.type === 'reactQuill' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
    >
      <TextField
        name={section.name}
        variant="outlined"
        margin="dense"
        value={section.value}
        onChange={(event) =>
          section.onChange
            ? section.onChange(event)
            : section.lotProcess
              ? onChangeLotProcess(event)
              : section.offer
                ? onChangeOffer(event)
                : section.asset
                  ? onChangeAsset(event)
                  : section.assumptions
                    ? onChangeAssumptions(event)
                    : section.lot
                      ? onChangeLot(event)
                      : section.details
                        ? onChangeProperty(event)
                        : onChangeAnalysis(event)
        }
        placeholder={replaceStrategyName(section.label, member)}
        label={replaceStrategyName(section.label, member)}
        required={
          section.required === true || section.required === 'true'
        }
        type="text"
        fullWidth={true}
        disabled={section.disabled || disabled}
        multiline
        rows={10}
        helperText={
          section.helper ? (
            <Typography
              color="primary"
              variant="caption"
              style={section.helperStyle ? section.helperStyle : null}
            >
              {section.helper}
            </Typography>
          ) : undefined
        }
      />
      {/* <ReactQuill
          theme="snow"
          defaultValue={section.value}
          onChange={(value) => {
            const regex = /<([^>]+)>/ig
            const event = {
              target: {
                name: section.name,
                value: replace(value, regex, ''),
                type: 'text'}
            }
            section.onChange ? section.onChange(event) : section.assumptions ? onChangeAssumptions(event) : section.lot ? onChangeLot(event) : section.details ? onChangeProperty(event) : onChangeAnalysis(event)
          }}
          placeholder="This is the property description..."
          style={{ height: 320 }}
        /> */}
    </Grid>
  ) : section.type === 'autocomplete' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
    >
      <Autocomplete
        id="checkboxes-tags-demo"
        options={section.options || []}
        value={section.value ?? null}
        disabled={section.disabled || disabled}
        onChange={(e, newValue) => {
          const event = {
            target: {
              name: section.name,
              value: newValue,
              type: 'text',
            },
            preventDefault: () => {
              return null
            },
          }
          section.lotProcess
            ? onChangeLotProcess(event)
            : section.offer
              ? onChangeOffer(event)
              : section.asset
                ? onChangeAsset(event)
                : section.assumptions
                  ? onChangeAssumptions(event)
                  : section.lot
                    ? onChangeLot(event)
                    : section.details
                      ? onChangeProperty(event)
                      : onChangeAnalysis(event)
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            label={replaceStrategyName(section.label, member)}
            placeholder={replaceStrategyName(section.label, member)}
            margin="dense"
          />
        )}
      />
    </Grid>
  ) : section.type === 'select' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      data-tour={section.dataTour}
    >
      <TextField
        name={section.name}
        select={!section.disabled}
        fullWidth={true}
        variant="outlined"
        margin="dense"
        label={section.label}
        value={resolveSelectValue(section)}
        helperText={
        <Typography
        color="primary"
        variant="caption"
        style={section.helperStyle ? section.helperStyle : null}
        >
        {section.helper}
        </Typography>
        }
        disabled={disabled || section.disabled}
        required={
        section.required === true || section.required === 'true'
        }
        onChange={(event) =>
        section.onChange
        ? section.onChange(event)
        : section.lotProcess
        ? onChangeLotProcess(event, true)
        : section.offer
        ? onChangeOffer(event, true)
        : section.asset
        ? onChangeAsset(event)
        : section.assumptions
        ? onChangeAssumptions(event, true)
        : section.lot
        ? onChangeLot(event, null, true)
        : section.details
        ? onChangeProperty(event, null, true)
        : onChangeAnalysis(event, null, true)
        }
        slotProps={{
        input: {
        startAdornment: section.adornmentNumber ? (
        <InputAdornment
        position="start"
        sx={{ marginRight: 0.25 }}
        >
        <Typography
        sx={{
        width: 20, // Smaller size
        height: 20,
        borderRadius: '50%',
        backgroundColor: 'black', // Black background
        color: 'white', // White text
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '0.75rem', // Smaller text
        padding: 0, // Remove padding
        textAlign: 'center', // Ensure centered alignment
        marginLeft: '-6px', // Adjust spacing on the left
        }}
        >
        {section.adornmentNumber}
        </Typography>
        </InputAdornment>
        ) : null,
        },
        select: {
        renderValue: (x) => {
        let option =
        find(
        concat(
        section.dynamicMemberOptions,
        section.memberOptions,
        section.options,
        ),
        { value: x },
        ) ||
        find(
        concat(
        section.dynamicMemberOptions,
        section.memberOptions,
        section.options,
        ),
        { value: Number(x) },
        )
        if (option)
        return option.active || isNil(option.active)
        ? option.label
        : `Deactivated - ${option.label}`
        else if (
        includes(
        concat(
        section.dynamicMemberOptions,
        section.memberOptions,
        section.options,
        ),
        x,
        )
        )
        return x
        else if (!x) return ''
        else return 'Deleted Option - ' + x
        },
        },
        }}
        >
        {map(
          filter(section.dynamicMemberOptions, (option) => {
            return (
              option.active ||
              isNil(option.active) ||
              option === section.value ||
              option.value === section.value ||
              Number(option.value) === section.value
            )
          }),
          (option) =>
            option.tip || option.title ? (
              <Tooltip
                title={option.tip}
                key={option.title}
                value={option.value}
              >
                <MenuItem>
                  <Grid
                    container
                    sx={{
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Grid>
                      <Typography>{option.title}</Typography>
                    </Grid>
                    <Grid>
                      <Typography>{option.label}</Typography>
                    </Grid>
                    <Grid>
                      <Typography>MO</Typography>
                    </Grid>
                  </Grid>
                </MenuItem>
              </Tooltip>
            ) : (
              <MenuItem key={option.value} value={option.value}>
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid>{option.label}</Grid>
                  <Typography style={{ flex: 1 }} />
                  <Grid>
                    <Typography>MO</Typography>
                  </Grid>
                </Grid>
              </MenuItem>
            ),
        )}
        {map(
          filter(section.memberOptions, (option) => {
            return (
              option.active ||
              isNil(option.active) ||
              option === section.value ||
              option.value === section.value ||
              Number(option.value) === section.value
            )
          }),
          (option) =>
            option.value ? (
              option.tip || option.title ? (
                <Tooltip
                  title={option.tip}
                  key={option.value}
                  value={option.value}
                  followCursor
                >
                  <MenuItem>
                    <Grid
                      container
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid>
                        <Typography>{option.title}</Typography>
                      </Grid>
                      <Grid>
                        <Typography>{option.label}</Typography>
                      </Grid>
                      <Typography>MO</Typography>
                    </Grid>
                  </MenuItem>
                </Tooltip>
              ) : (
                <MenuItem key={option.value} value={option.value}>
                  <Grid
                    container
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Grid>{option.label}</Grid>
                    <Typography style={{ flex: 1 }} />
                    <Typography>MO</Typography>
                  </Grid>
                </MenuItem>
              )
            ) : (
              <MenuItem key={option} value={option}>
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid>{option}</Grid>
                  <Typography style={{ flex: 1 }} />
                  <Typography>MO</Typography>
                </Grid>
              </MenuItem>
            ),
        )}
        {map(
          filter(section.options, (option) => {
            return (
              option.active ||
              isNil(option.active) ||
              option === section.value ||
              option.value === section.value ||
              Number(option.value) === Number(section.value)
            )
          }),
          (option) =>
            option.value ? (
              option.tip || option.title ? (
                <Tooltip
                  title={option.tip}
                  key={option.value}
                  value={option.value}
                  followCursor
                >
                  <MenuItem>
                    <Grid
                      container
                      sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                      <Grid>
                        <Typography>{option.title}</Typography>
                      </Grid>
                      <Grid>
                        <Typography>{option.label}</Typography>
                      </Grid>
                      <Grid>
                        {section.editOption &&
                          (option.active || isNil(option.active)) && (
                            <AddOption
                              currentValue={
                                includes(
                                  section.options,
                                  section.value,
                                ) ||
                                find(section.options, {
                                  value: section.value,
                                })
                                  ? section.value
                                  : ''
                              }
                              edit
                              currentOption={option}
                              addOption={section.optionName}
                              addOptionLabel={section.label}
                              addOptionTarget={section.name}
                              onChangeAnalysis={onChangeAnalysis}
                              onChangeLotProcess={onChangeLotProcess}
                              onChangeProperty={onChangeProperty}
                              onChangeOffer={onChangeOffer}
                              onChangeAssumptions={
                                onChangeAssumptions
                              }
                            />
                          )}
                        {section.removeOption &&
                          (option.active || isNil(option.active)) && (
                            <Grid>
                              <RemoveOption
                                removeOption={section.optionName}
                                removeOptionLabel={section.label}
                                removeOptionValue={option.value}
                                removeOptionItemLabel={option.title}
                              />
                            </Grid>
                          )}
                      </Grid>
                    </Grid>
                  </MenuItem>
                </Tooltip>
              ) : (
                <MenuItem key={option.value} value={option.value}>
                  <Grid
                    container
                    sx={{
                      alignItems: 'center',
                    }}
                  >
                    <Grid>{option.label}</Grid>
                    <Typography style={{ flex: 1 }} />
                    {section.editOption &&
                      (option.active || isNil(option.active)) && (
                        <AddOption
                          currentValue={
                            includes(
                              section.options,
                              section.value,
                            ) ||
                            find(section.options, {
                              value: section.value,
                            })
                              ? section.value
                              : ''
                          }
                          edit
                          currentOption={option}
                          addOption={section.optionName}
                          addOptionLabel={section.label}
                          addOptionTarget={section.name}
                          onChangeAnalysis={onChangeAnalysis}
                          onChangeLotProcess={onChangeLotProcess}
                          onChangeProperty={onChangeProperty}
                          onChangeOffer={onChangeOffer}
                          onChangeAssumptions={onChangeAssumptions}
                        />
                      )}
                    {section.removeOption &&
                      (option.active || isNil(option.active)) && (
                        <Grid>
                          <RemoveOption
                            removeOption={section.optionName}
                            removeOptionLabel={section.label}
                            removeOptionValue={option.value}
                            removeOptionItemLabel={option.title}
                          />
                        </Grid>
                      )}
                  </Grid>
                </MenuItem>
              )
            ) : (
              <MenuItem key={option} value={option}>
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid>{option}</Grid>
                  <Typography style={{ flex: 1 }} />
                  {section.editOption &&
                    (option.active || isNil(option.active)) && (
                      <AddOption
                        currentValue={
                          includes(section.options, section.value) ||
                          find(section.options, {
                            value: section.value,
                          })
                            ? section.value
                            : ''
                        }
                        edit
                        currentOption={option}
                        addOption={section.optionName}
                        addOptionLabel={section.label}
                        addOptionTarget={section.name}
                        onChangeAnalysis={onChangeAnalysis}
                        onChangeLotProcess={onChangeLotProcess}
                        onChangeProperty={onChangeProperty}
                        onChangeOffer={onChangeOffer}
                        onChangeAssumptions={onChangeAssumptions}
                      />
                    )}
                  {section.removeOption &&
                    (option.active || isNil(option.active)) && (
                      <Grid>
                        <RemoveOption
                          removeOption={section.optionName}
                          removeOptionLabel={section.label}
                          removeOptionValue={option}
                        />
                      </Grid>
                    )}
                </Grid>
              </MenuItem>
            ),
        )}
        {section.optionName && (
          <AddOption
            currentValue={
              includes(section.options, section.value) ||
              find(section.options, { value: section.value  })
                ? section.value
                : ''
            }
            addOption={section.optionName}
            addOptionLabel={section.label}
            addOptionTarget={section.name}
            onChangeAnalysis={onChangeAnalysis}
            onChangeLotProcess={onChangeLotProcess}
            onChangeProperty={onChangeProperty}
            onChangeOffer={onChangeOffer}
            onChangeAssumptions={onChangeAssumptions}
          />
        )}
      </TextField>
    </Grid>
  ) : // )
  section.dropdown ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      data-tour={section.dataTour}
    >
      <NumericFormat
        customInput={TextField}
        thousandSeparator={section.adornment === '$'}
        prefix={section.adornment === '$' ? '$' : ''}
        suffix={section.adornment === '%' ? '%' : ''}
        fullWidth={true}
        // decimalScale={0}
        key={section.name}
        name={section.name}
        value={Math.round(section.value) || ''}
        disabled={section.disabled || disabled}
        onValueChange={(values) => {
        const { value } = values
        const event = {
        target: {
        name: section.name,
        value: value,
        type: 'number',
        },
        preventDefault: () => {
        return null
        },
        }
        if (section.disabled || disabled) return null
        if (section.onChange) section.onChange(event)
        else if (section.lotProcess) onChangeLotProcess(event)
        else if (section.offer) onChangeOffer(event)
        else if (section.asset) onChangeAsset(event)
        else if (section.assumptions) onChangeAssumptions(event)
        else if (section.lot) onChangeLot(event)
        else if (section.details)
        onChangeProperty(
        event,
        section.definition ? 'userInput' : null,
        )
        else
        onChangeAnalysis(
        event,
        section.definition ? 'userInput' : null,
        )
        //onChange(event, saveAnalysis)
        }}
        type={'text'}
        onFocus={(event) => {
        event.target.select()
        }}
        variant="outlined"
        margin="dense"
        placeholder={replaceStrategyName(section.label, member)}
        label={replaceStrategyName(section.label, member)}
        fontSize={section.font}
        required={
        section.required === true || section.required === 'true'
        }
        helperText={
        <Typography
        color="primary"
        variant="caption"
        style={section.helperStyle ? section.helperStyle : null}
        >
        {section.helper}
        </Typography>
        }
        slotProps={{
        input: {
        startAdornment: section.adornmentNumber ? (
        <InputAdornment
        position="start"
        sx={{ marginRight: 0.25 }}
        >
        <Typography
        sx={{
        width: 20, // Smaller size
        height: 20,
        borderRadius: '50%',
        backgroundColor: 'black', // Black background
        color: 'white', // White text
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '0.75rem', // Smaller text
        padding: 0, // Remove padding
        textAlign: 'center', // Ensure centered alignment
        marginLeft: '-6px', // Adjust spacing on the left
        }}
        >
        {section.adornmentNumber}
        </Typography>
        </InputAdornment>
        ) : null,
        endAdornment: (
        <InputAdornment position="end" margin="dense">
        <Button
        onClick={(e) =>
        section.disabled || disabled
        ? null
        : toggleAssumptionsMenuFormat(
        e,
        section.adornmentName,
        )
        }
        size="small"
        margin="dense"
        style={{ padding: 3 }}
        sx={{
          backgroundColor: "rgba(79, 80, 98, 0.15)",
          color: "#4f5062",
          borderColor: "transparent",
          "&:hover, &.active": { backgroundColor: "#4f5062", color: "#fff" },
          "&:hover .btn-wrapper--icon svg path, &.active .btn-wrapper--icon svg path": { fill: "#fff" },
          "html[data-color-mode=\"dark\"] &": { background: "rgba(255, 255, 255, 0.08) !important", color: "rgba(255, 255, 255, 0.92) !important" },
          display: "flex",
          alignItems: "center",
        }}
        >
        <Box component="span" sx={{
          display: "inline-block",
          "&:not(:first-of-type)": { ml: "0.5rem", marginLeft: "0.5rem" },
          "& + .btn-wrapper--icon": { ml: "0.5rem", marginLeft: "0.5rem" },
        }}>
        {section.adornment}
        </Box>
        <Box component="span" sx={[{ display: 'flex' }, { display: "inline-flex" }]}>
        <FontAwesomeIcon icon={['fas', 'chevron-down']} style={{ ml: '0.25rem' }} sx={{ opacity: "0.8 !important", fontSize: "0.8333333333rem !important" }} />
        </Box>
        </Button>
        {showDefinitionLookup ? (
        <InputAdornment position="end">
        <DefinitionLookupButton
        section={section}
        disabled={disabled}
        handleDefinition={handleDefinition}
        />
        </InputAdornment>
        ) : null}
        <Menu
        anchorEl={anchorElAssumptions}
        keepMounted
        name={section.adornmentName}
        open={
        Boolean(anchorElAssumptions) &&
        section.adornmentName === showFormatDropdown
        }
        onClose={handleAssumptionsMenuClose}
        anchorOrigin={{
        vertical: 'center',
        horizontal: 'center',
        }}
        transformOrigin={{
        vertical: 'center',
        horizontal: 'center',
        }}
        slotProps={{ list: { sx: { p: 0 } } }}
        >
        <Box sx={{ p: '0.5rem' }}>
        <List component="div" sx={[{
          width: "100%",
          "& .MuiListItem-root, & .MuiListItemButton-root": { borderRadius: "0.65rem", width: "100%" },
          "& .MuiListItem-root, & .MuiListItemButton-root, .MuiList-root.nav-transparent-alt .MuiListItem-root, .MuiList-root.nav-transparent-alt .MuiListItemButton-root": { width: "100%" },
          "& .MuiListItem-root.MuiListItem-button, & .MuiListItemButton-root": { borderRadius: "0.65rem", width: "100%" },
        }, { p: 0, m: 0, flexDirection: 'column' }, {
          "& .MuiListItem-root.MuiListItem-button.Mui-selected, & .MuiListItem-root.MuiListItem-button:hover, & .MuiListItemButton-root.Mui-selected, & .MuiListItemButton-root:hover": {
            color: "#4f5062",
            background: "rgba(79, 80, 98, 0.05)",
            "&::before": { background: "#4f5062" },
          },
        }]}>
        <ListItemButton
        key="$"
        onClick={(e) =>
        section.assumptions
        ? handleAdornmentChange(
        e,
        section.adornmentName,
        includes(
        section.adornmentName,
        'term',
        ) ||
        includes(
        section.adornmentName,
        'Term',
        )
        ? 'Months'
        : section.adornmentName ===
        'estimatedRehabType'
        ? 'SQFT $'
        : includes(
        section.adornmentName,
        'avgRateType',
        )
        ? 'Daily'
        : '$',
        )
        : handleAnalysisAdornmentChange(
        e,
        section.adornmentName,
        includes(section.adornmentName, 'HOA')
        ? 'Monthly'
        : '$',
        )
        }
        selected={
        section.adornment === '$' ||
        section.adornment === 'SQFT $' ||
        section.adornment === 'Months' ||
        section.adornment === 'Monthly'
        }
        sx={{ px: '1rem', mx: '0.5rem' }}
        >
        <span>
        {section.assumptions
        ? includes(
        section.adornmentName,
        'term',
        ) ||
        includes(section.adornmentName, 'Term')
        ? 'Months'
        : section.adornmentName ===
        'estimatedRehabType'
        ? 'SQFT $'
        : includes(
        section.adornmentName,
        'avgRateType',
        )
        ? 'Daily'
        : '$'
        : includes(section.adornmentName, 'HOA')
        ? 'Monthly'
        : '$'}
        </span>
        </ListItemButton>
        <ListItemButton
        key="%"
        onClick={(e) =>
        section.assumptions
        ? handleAdornmentChange(
        e,
        section.adornmentName,
        includes(
        section.adornmentName,
        'term',
        ) ||
        includes(
        section.adornmentName,
        'Term',
        )
        ? 'Years'
        : section.adornmentName ===
        'estimatedRehabType'
        ? 'Total $'
        : includes(
        section.adornmentName,
        'avgRateType',
        )
        ? 'Weekly'
        : '%',
        )
        : handleAnalysisAdornmentChange(
        e,
        section.adornmentName,
        includes(section.adornmentName, 'HOA')
        ? 'Yearly'
        : '%',
        )
        }
        selected={
        section.adornment === '%' ||
        section.adornment === 'Total $' ||
        section.adornment === 'Years' ||
        section.adornment === 'Yearly'
        }
        sx={{ px: '1rem', mx: '0.5rem' }}
        >
        <span>
        {section.assumptions
        ? includes(
        section.adornmentName,
        'term',
        ) ||
        includes(section.adornmentName, 'Term')
        ? 'Years'
        : section.adornmentName ===
        'estimatedRehabType'
        ? 'Total $'
        : includes(
        section.adornmentName,
        'avgRateType',
        )
        ? 'Weekly'
        : '%'
        : includes(section.adornmentName, 'HOA')
        ? 'Yearly'
        : '%'}
        </span>
        </ListItemButton>
        </List>
        </Box>
        </Menu>
        </InputAdornment>
        ),
        },
        }}
        />
    </Grid>
  ) : section.dropdownDate ? (
    <Grid style={{ marginRight: 5 }}>
      <TextField
        select
        variant="outlined"
        margin="dense"
        label="Summary Date"
        fullWidth={true}
        style={{ width: 175 }}
        value={section.value}
        onChange={section.onChange}
        required={true}
      >
        <MenuItem /*key={'Fix & Flip'}*/ value={'Current Month'}>
          {'Current Month'}
        </MenuItem>
        <MenuItem /*key={'Buy & Hold'}*/ value={'YTD'}>
          {'YTD'}
        </MenuItem>
        <MenuItem /*key={'Buy & Hold'}*/ value={'12 Month Rolling'}>
          {'12 Month Rolling'}
        </MenuItem>
      </TextField>
    </Grid>
  ) : section.name === 'exchange' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      container
      direction="row"
      sx={{
        alignItems: 'center',
      }}
    >
      <Grid>
        <Checkbox
          color="primary"
          key={section.name + '_key'}
          name={section.name}
          type="checkbox"
          value={section.value}
          checked={section.value}
          required={
            section.required === true || section.required === 'true'
          }
          onChange={(event) =>
            section.disabled || disabled
              ? null
              : onChangeExchange(event, section.label, true)
          }
        />
      </Grid>
      <Grid>
        <Typography>
          {replaceStrategyName(section.label, member)}
        </Typography>
      </Grid>
    </Grid>
  ) : section.type === 'checkbox' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      data-tour={section.dataTour}
    >
      <FormControlLabel
        onChange={
          section.onChange
            ? section.onChange
            : section.lotProcess
              ? (event) => onChangeLotProcess(event, true)
              : section.offer
                ? (event) => onChangeOffer(event, true)
                : section.asset
                  ? onChangeAsset
                  : section.assumptions
                    ? (event) => onChangeAssumptions(event, true)
                    : section.lot
                      ? (event) => onChangeLot(event, null, true)
                      : section.details
                        ? (event) =>
                            onChangeProperty(event, null, true)
                        : (event) =>
                            onChangeAnalysis(event, null, true)
        }
        control={
          <Checkbox
            id={section.label}
            checked={Boolean(section.value)}
            name={section.name}
          />
        }
        label={replaceStrategyName(section.label, member)}
        disabled={section.disabled || disabled}
        required={
          section.required === true || section.required === 'true'
        }
      />
    </Grid>
  ) : section.type === 'dollar' ? (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      data-tour={section.dataTour}
    >
      <NumericFormat
        customInput={TextField}
        thousandSeparator={true}
        prefix={'$'}
        fullWidth={true}
        decimalScale={0}
        key={section.name}
        name={section.name}
        value={Math.round(section.value) || ''}
        disabled={section.disabled || disabled}
        onValueChange={(values) => {
          const { value } = values
          const event = {
            target: {
              name: section.name,
              value: value,
              type: 'number',
            },
            preventDefault: () => {
              return null
            },
          }
          if (section.disabled || disabled) return null
          if (section.onChange) section.onChange(event)
          else if (section.lotProcess) onChangeLotProcess(event)
          else if (section.offer) onChangeOffer(event)
          else if (section.asset) onChangeAsset(event)
          else if (section.assumptions) onChangeAssumptions(event)
          else if (section.lot) onChangeLot(event)
          else if (section.details)
            onChangeProperty(
              event,
              section.definition ? 'userInput' : null,
            )
          else
            onChangeAnalysis(
              event,
              section.definition ? 'userInput' : null,
            )
          //onChange(event, saveAnalysis)
        }}
        type={'text'}
        onFocus={(event) => {
          event.target.select()
        }}
        variant="outlined"
        margin="dense"
        label={replaceStrategyName(section.label, member)}
        fontSize={section.font}
        required={
          section.required === true || section.required === 'true'
        }
        helperText={
          <Typography
            color="primary"
            variant="caption"
            style={section.helperStyle ? section.helperStyle : null}
          >
            {section.helper}
          </Typography>
        }
        slotProps={{
          input: {
            // inputComponent: NumericFormatCustom, // Use the custom NumericFormat component
            startAdornment: section.adornmentNumber ? (
              <InputAdornment
                position="start"
                sx={{ marginRight: 0.25 }}
              >
                <Typography
                  sx={{
                    width: 20, // Smaller size
                    height: 20,
                    borderRadius: '50%',
                    backgroundColor: 'black', // Black background
                    color: 'white', // White text
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    fontSize: '0.75rem', // Smaller text
                    padding: 0, // Remove padding
                    textAlign: 'center', // Ensure centered alignment
                    marginLeft: '-6px', // Adjust spacing on the left
                  }}
                >
                  {section.adornmentNumber}
                </Typography>
              </InputAdornment>
            ) : null,
            // endAdornment: (
            //   <InputAdornment
            //     position="end"
            //     sx={{ marginRight: '-8px' }} // Reduce spacing on the right
            //   >
            //     <Tooltip title={''} followCursor>
            //       <IconButton
            //
            //         size="large"
            //       >
            //         <FontAwesomeIcon
            //           icon={['fas', 'database']}
            //
            //         />
            //       </IconButton>
            //     </Tooltip>
            //   </InputAdornment>
            // ),
            endAdornment: showDefinitionLookup ? (
              <InputAdornment position="end">
                <DefinitionLookupButton
                  section={section}
                  disabled={disabled}
                  handleDefinition={handleDefinition}
                  dataTour={section.dataTourDatabase}
                />
              </InputAdornment>
            ) : null,
          },
        }}
      />
    </Grid>
  ) : section.type === 'datePicker' ? (
    <Grid size={{ xs: section.xs, lg: section.lg }}>
      <DatePicker
        name={section.name}
        label={replaceStrategyName(section.label, member)}
        value={section.value ? section.value : null}
        minDate={section.min}
        maxDate={section.max}
        format="MM/dd/yyyy"
        required={
          section.required === true || section.required === 'true'
        }
        onChange={(newValue) => {
          const event = {
            target: {
              name: section.name,
              value: newValue,
              type: 'date',
            },
            preventDefault: () => {
              return null
            },
          }
          if (section.disabled || disabled) return null
          if (section.onChange) section.onChange(event)
          else if (section.lotProcess) onChangeLotProcess(event)
          else if (section.offer) onChangeOffer(event)
          else if (section.asset) onChangeAsset(event)
          else if (section.assumptions) onChangeAssumptions(event)
          else if (section.lot) onChangeLot(event)
          else if (section.details) onChangeProperty(event)
          else onChangeAnalysis(event)
          //onChange(event, saveAnalysis)
        }}
        fullWidth={true}
        sx={{ width: '100% ' }}
        slotProps={{
          textField: {
            variant: 'outlined',
            margin: 'dense',
            required:
              section.required === true ||
              section.required === 'true',
          },
        }}
      />
    </Grid>
  ) : section.type === 'date' ? (
    <Grid
      size={{ xs: section.xs, lg: section.lg }}
      data-tour={section.dataTour}
    >
      <DatePicker
        name={section.name}
        label={replaceStrategyName(section.label, member)}
        value={section.value ? section.value : null}
        minDate={section.min}
        maxDate={section.max}
        format="MM/dd/yyyy"
        required={
          section.required === true || section.required === 'true'
        }
        fullWidth={true}
        sx={{ width: '100% ' }}
        onChange={(newValue) => {
          const event = {
            target: {
              name: section.name,
              value: newValue,
              type: 'date',
            },
            preventDefault: () => {
              return null
            },
          }
          if (section.disabled || disabled) return null
          if (section.onChange) section.onChange(event)
          else if (section.lotProcess) onChangeLotProcess(event)
          else if (section.offer) onChangeOffer(event)
          else if (section.asset) onChangeAsset(event)
          else if (section.assumptions) onChangeAssumptions(event)
          else if (section.lot) onChangeLot(event)
          else if (section.details) onChangeProperty(event)
          else onChangeAnalysis(event)
          //onChange(event, saveAnalysis)
        }}
        slotProps={{
          textField: {
            variant: 'outlined',
            margin: 'dense',
            required:
              section.required === true ||
              section.required === 'true',
            fullWidth: true,
          },
        }}
      />
    </Grid>
  ) : (
    <Grid
      size={{
        xs: section.xs,
        sm: section.sm,
        md: section.md,
        lg: section.lg,
        xl: section.xl,
      }}
      data-tour={section.dataTour}
      //alignItems='center'
    >
      <TextField
        name={section.name}
        variant="outlined"
        margin="dense"
        value={section.value || ''}
        onChange={(event) =>
        section.onChange
        ? section.onChange(event)
        : section.lotProcess
        ? onChangeLotProcess(event)
        : section.offer
        ? onChangeOffer(event)
        : section.asset
        ? onChangeAsset(event)
        : section.assumptions
        ? onChangeAssumptions(event)
        : section.lot
        ? onChangeLot(event)
        : section.details
        ? onChangeProperty(event)
        : onChangeAnalysis(event)
        }
        onBlur={section.onBlur ? section.onBlur : null}
        placeholder={replaceStrategyName(section.label, member)}
        label={replaceStrategyName(section.label, member)}
        type={section.type === 'percentage' ? 'number' : section.type}
        fullWidth={true}
        disabled={section.disabled || disabled}
        required={
        section.required === true || section.required === 'true'
        }
        helperText={
        <Typography
        color="primary"
        variant="caption"
        style={section.helperStyle ? section.helperStyle : null}
        >
        {section.helper}
        </Typography>
        }
        slotProps={{
        input: {
        startAdornment: section.adornmentNumber ? (
        <InputAdornment
        position="start"
        sx={{ marginRight: 0.25 }}
        >
        <Typography
        sx={{
        width: 20, // Smaller size
        height: 20,
        borderRadius: '50%',
        backgroundColor: 'black', // Black background
        color: 'white', // White text
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '0.75rem', // Smaller text
        padding: 0, // Remove padding
        textAlign: 'center', // Ensure centered alignment
        marginLeft: '-6px', // Adjust spacing on the left
        }}
        >
        {section.adornmentNumber}
        </Typography>
        </InputAdornment>
        ) : null,
        endAdornment:
        section.type === 'percentage' ? (
        <>
        <InputAdornment position="end">%</InputAdornment>
        {showDefinitionLookup ? (
        <InputAdornment position="end">
        <DefinitionLookupButton
        section={section}
        disabled={disabled}
        handleDefinition={handleDefinition}
        />
        </InputAdornment>
        ) : null}
        </>
        ) : section.adornmentPosition === 'end' ? (
        <>
        <InputAdornment position="end">
        {section.adornment}
        </InputAdornment>
        {showDefinitionLookup ? (
        <InputAdornment position="end">
        <DefinitionLookupButton
        section={section}
        disabled={disabled}
        handleDefinition={handleDefinition}
        />
        </InputAdornment>
        ) : null}
        </>
        ) : null,
        },
        inputLabel: {
        shrink: section.type === 'date' || section.value,
        },
        }}
        />
    </Grid>
  )
}

const renderInputFieldsFunction =
  (onChangeFunctions, dropdownProps, member) =>
  (disabled) =>
  (section) => {
    const fieldKey = section.name || section.label
    if (section.tooltipField)
      return (
        <Fragment key={fieldKey}>
          {/* <Tooltip
          title={
            dropdownProps &&
            Boolean(dropdownProps.anchorElAssumptions)
              ? ''
              : section.tooltipField
          }
          followCursor
        > */}
          {renderInput(
            section,
            member,
            disabled,
            onChangeFunctions,
            dropdownProps,
          )}
          {/* </Tooltip> */}
        </Fragment>
      )
    return (
      <Fragment key={fieldKey}>
        {renderInput(
          section,
          member,
          disabled,
          onChangeFunctions,
          dropdownProps,
        )}
      </Fragment>
    )
  }

export default renderInputFieldsFunction
